<template>
  <a-dialog ref="dialog" name="change_plan" :loading="loading" @open="opened" @close="$reset">
    <span slot="title" v-if="estimate">
      {{ estimate.downgrade ? $t('offer.downgrade') : $t('offer.upgrade') }}
    </span>
    <div slot="content" v-if="estimate">
      <div v-if="estimate.downgrade">
        {{ $t('offer.downgrade-renew') }} {{ $helpers.present.date(subscription.period.end) }}.
      </div>
      <div v-else>
        {{ $t('offer.upgrade-renew') }} {{ $helpers.present.date(subscription.period.end) }}.
      </div>

      <a-payment-method-form
        v-show="!estimate.downgrade"
        ref="payment_method"
        @valid="onPaymentValid"
        @message="onFormMessage">
      </a-payment-method-form>

      <div v-if="estimate.downgrade">
        <v-checkbox
          color="error"
          v-model="immediateDowngrade"
          :label="$t('edit-offer')">
        </v-checkbox>

        <v-alert class="mb-0" color="error" text border="left" v-if="immediateDowngrade">
          {{ $t('warning-edit') }}
        </v-alert>
      </div>

      <div v-if="estimate.tax > 0" class="mt-4 blue--text text--darken-2">
        <v-icon left small color="blue darken-2">mdi-lightbulb-on-outline</v-icon>
        <span v-html="$t('tax-added')"></span>
      </div>
    </div>
    <span slot="actions" v-if="estimate">
      <v-btn v-if="estimate.downgrade" :color="immediateDowngrade ? 'error' : 'warning'" text @click="submit()">
        <span v-if="immediateDowngrade">{{ $t('immediate-downgrade') }}</span>
        <span v-else>{{ $t('downgrade-at-renew') }}</span>
      </v-btn>
      <v-btn v-else color="success" :disabled="!valid" text @click="submit()">
        <v-icon left>mdi-lock</v-icon>
        {{ $t('actions.pay') }} {{
          $helpers.present.currency(estimate.total / 100)
        }} {{ estimate.tax > 0 ? $t('prices.with-taxes') : $t('prices.without-taxes') }}
      </v-btn>
    </span>
  </a-dialog>
</template>

<script>
export default {
  name: 'ChangePlanDialog',
  data () {
    return {
      loading: '',
      valid: true,
      immediateDowngrade: false,
      subscription: null,
      estimate: null
    }
  },
  methods: {
    onPaymentValid (valid) {
      this.valid = valid
    },
    onFormMessage (message) {
      this.loading = message
    },
    async opened (subscription) {
      this.subscription = subscription
      try {
        this.loading = this.$t('edit-estimation')
        this.estimate = (await this.$api.post('account/customer/subscription/estimate', this.subscription.toArray())).data
      } finally {
        this.loading = ''
      }
    },
    submit () {
      this.loading = this.$t('create-order')
      this.subscription.update(this.immediateDowngrade)
        .then(response => {
          if (response.incomplete_payment) {
            this.validateIncompletePayment(response.incomplete_payment)
          } else if (response.payment_completed) {
            this.refreshAccountInformations().then(() => {
              this.closeDialogWithSuccess(this.$t('success-payment'))
            })
          } else {
            this.loading = ''
            this.$ui.error(this.$t('warning-error'))
          }
        })
        .catch(() => this.loading = '')
    },

    /*
     * COMMON ELEMENTS FOR PAYMENT MANAGMENT
     */
    validateIncompletePayment (incompletePayment) {
      this.$refs.payment_method.pay(incompletePayment.client_secret, incompletePayment.payment_method)
        .then(() => this.waitForSubscriptionAndCloseDialog())
        .catch(err => {
          this.refreshAccountInformations()
            .then(() => this.closeDialogWithError(err))
        })
    },

    // Dialog control / action outcome control
    closeDialogWithSuccess (message) {
      this.$ui.success(message)
      this.loading = ''
      this.closeDialog()
    },
    closeDialogWithError (message) {
      this.$ui.warning(message)
      this.loading = ''
      this.closeDialog()
    },
    closeDialogWithTimeout () {
      this.$ui.warning(this.$t('payment-loading-refresh'))
      this.loading = ''
      this.closeDialog()
    },
    closeDialog () {
      if (this.$refs.dialog) this.$refs.dialog.close()
    },

    // Helpers
    refreshAccountInformations () {
      this.loading = this.$t('subscription-update')
      return new Promise(resolve => {
        this.$account.fetch({ showLoading: false }).then(() => resolve())
      })
    },
    waitForSubscriptionAndCloseDialog () {
      this.loading = this.$t('payment-loading')
      this.waitForSubscription()
        .then(success => {
          if (success) this.closeDialogWithSuccess(this.$t('success-payment'))
          else this.closeDialogWithTimeout()
        })
    },
    waitForSubscription () {
      return new Promise(resolve => {
        let i = 0

        const interval = setInterval(() => {
          if (i++ >= 4) {
            clearInterval(interval)
            resolve(false)
          } else {
            this.$account.fetch({ showLoading: false })
              .then(() => {
                const subscription = this.$account.getSubscription('check-position')
                if (subscription.name !== 'default' && subscription.status === 'active') {
                  clearInterval(interval)
                  resolve(true)
                }
              })
              .catch(() => {
                clearInterval(interval)
                resolve(false)
              })
          }
        }, 5 * 1000)
      })
    },
  }
}
</script>

<style scoped>

</style>

<i18n>
{
	"en": {
		"tax-added": "A 20% VAT is applied to your account. If you are a company outside France with a VAT number <a href=\"mailto:hello@check-position.com\">contact us</a> specifying your country and your Intracom VAT number.",
		"create-order": "Creating your order ...",
		"downgrade-at-renew": "Decrease at renewal",
		"edit-estimation": "Estimation of modifications ...",
		"edit-offer": "I would like an immediate modification of my offer.",
		"immediate-downgrade": "Decrease immediately",
		"offer": {
			"downgrade": "Decrease an offer",
			"downgrade-renew": "Your offer will be reduced at the next renewal on",
			"upgrade": "Increase an offer",
			"upgrade-renew": "The increase in your offer is instantaneous and issues an invoice in proportion to the time remaining until the next renewal on"
		},
		"payment-loading": "Waiting for payment (please wait, it may take several seconds) ...",
		"payment-loading-refresh": "Payment is still pending, please wait a few minutes then refresh the page.",
		"subscription-update": "Updating your subscription ...",
		"success-payment": "Payment successfully validated!",
		"warning-edit": "Please note, an immediate modification of your offer is free and instantaneous, but does not refund any of the remaining period.",
		"warning-error": "An unknown error has occurred. Please try again in a few moments."
	},
	"es": {
		"tax-added": "Se aplica un 20% de IVA a su cuenta. Si es una empresa fuera de Francia con un número de IVA <a href=\"mailto:hello@check-position.com\">contáctenos</a> especificando su país y su número de IVA intracomunitario.",
		"create-order": "Creando su pedido ...",
		"downgrade-at-renew": "Disminución en la renovación",
		"edit-estimation": "Estimación de modificaciones ...",
		"edit-offer": "Me gustaría una modificación inmediata de mi oferta.",
		"immediate-downgrade": "Disminuir inmediatamente",
		"offer": {
			"downgrade": "Disminuir una oferta",
			"downgrade-renew": "Su oferta se reducirá en la próxima renovación el",
			"upgrade": "Incrementar una oferta",
			"upgrade-renew": "El aumento de su oferta es instantáneo y emite una factura en proporción al tiempo restante hasta la próxima renovación el"
		},
		"payment-loading": "Esperando el pago (espere, puede tardar varios segundos) ...",
		"payment-loading-refresh": "El pago aún está pendiente, espere unos minutos y luego actualice la página.",
		"subscription-update": "Actualizando su suscripción ...",
		"success-payment": "Pago validado con éxito!",
		"warning-edit": "Tenga en cuenta que una modificación inmediata de su oferta es gratuita e instantánea, pero no implica ningún reembolso del período restante.",
		"warning-error": "Un error desconocido a ocurrido. Vuelva a intentarlo en unos momentos."
	},
	"fr": {
		"tax-added": "Une TVA de 20% est appliquée sur votre compte. Si vous êtes une entreprise hors France disposant d'un numéro de TVA Intracommunautaire <a href=\"mailto:bonjour@check-position.com\">contactez nous</a> en spécifiant votre pays et votre numéro de TVA Intracom.",
		"create-order": "Création de votre commande ...",
		"downgrade-at-renew": "Diminuer au renouvellement",
		"edit-estimation": "Estimation des modifications ...",
		"edit-offer": "Je souhaite une modification immédiate de mon offre.",
		"immediate-downgrade": "Diminuer immédiatement",
		"offer": {
			"downgrade": "Diminuer une offre",
			"downgrade-renew": "La diminution de votre offre s'effectuera au prochain renouvellement le ",
			"upgrade": "Augmenter une offre",
			"upgrade-renew": "L'augmentation de votre offre est instantannée et édite une facture au prorata du temps restant jusqu'au prochain renouvellement le "
		},
		"payment-loading": "En attente du paiement (merci de patienter, cela peut prendre plusieurs secondes) ...",
		"payment-loading-refresh": "Le paiement est toujours en attente, merci de patienter quelques minutes puis actualiser la page.",
		"subscription-update": "Mise à jour de votre souscription ...",
		"success-payment": "Paiement validé avec succès !",
		"warning-edit": "Attention, une modification immédiate de votre offre est gratuite et instantanée, mais n'entraine aucun remboursement de la période restante.",
		"warning-error": "Une erreur inconnue est survenue. Merci de tenter à nouveau dans quelques instants."
	},
	"it": {
		"tax-added": "Sul tuo account viene applicata un'IVA del 20%. Se sei un'azienda fuori dalla Francia con un numero di partita IVA <a href=\"mailto:hello@check-position.com\">contattaci</a> specificando il tuo paese e il tuo numero IVA Intracom.",
		"create-order": "Creazione del tuo ordine ...",
		"downgrade-at-renew": "Riduzione al rinnovo",
		"edit-estimation": "Stima delle modifiche ...",
		"edit-offer": "Desidero una modifica immediata della mia offerta.",
		"immediate-downgrade": "Riduci immediatamente",
		"offer": {
			"downgrade": "Riduci un'offerta",
			"downgrade-renew": "La tua offerta sarà ridotta al prossimo rinnovo il",
			"upgrade": "Aumenta un'offerta",
			"upgrade-renew": "L'aumento della tua offerta è istantaneo e genera una fattura proporzionale al tempo rimanente fino al prossimo rinnovo il"
		},
		"payment-loading": "In attesa del pagamento (attendi, potrebbe richiedere qualche secondo) ...",
		"payment-loading-refresh": "Il pagamento è ancora in attesa, attendi qualche minuto e poi aggiorna la pagina.",
		"subscription-update": "Aggiornamento del tuo abbonamento ...",
		"success-payment": "Pagamento convalidato con successo!",
		"warning-edit": "Attenzione, una modifica immediata della tua offerta è gratuita e istantanea, ma non comporta alcun rimborso del periodo rimanente.",
		"warning-error": "Si è verificato un errore sconosciuto. Riprova tra qualche momento."
	},
	"pt": {
		"tax-added": "É aplicado um IVA de 20% à sua conta. Se for uma empresa fora de França com um número de IVA <a href=\"mailto:hello@check-position.com\">contacte-nos</a> especificando o seu país e o seu número de IVA Intracom.",
		"create-order": "A criar o seu pedido ...",
		"downgrade-at-renew": "Redução na renovação",
		"edit-estimation": "Estimativa das alterações ...",
		"edit-offer": "Gostaria de uma modificação imediata da minha oferta.",
		"immediate-downgrade": "Reduzir imediatamente",
		"offer": {
			"downgrade": "Reduzir uma oferta",
			"downgrade-renew": "A sua oferta será reduzida na próxima renovação em",
			"upgrade": "Aumentar uma oferta",
			"upgrade-renew": "O aumento da sua oferta é instantâneo e emite uma fatura proporcional ao tempo restante até à próxima renovação em"
		},
		"payment-loading": "À espera do pagamento (por favor aguarde, pode demorar alguns segundos) ...",
		"payment-loading-refresh": "O pagamento ainda está pendente, por favor aguarde alguns minutos e depois atualize a página.",
		"subscription-update": "A atualizar a sua subscrição ...",
		"success-payment": "Pagamento validado com sucesso!",
		"warning-edit": "Atenção, uma modificação imediata da sua oferta é gratuita e instantânea, mas não implica qualquer reembolso do período restante.",
		"warning-error": "Ocorreu um erro desconhecido. Por favor tente novamente em alguns instantes."
	},
	"pl": {
		"tax-added": "Do Twojego konta stosowany jest 20% podatek VAT. Jeśli jesteś firmą spoza Francji z numerem VAT <a href=\"mailto:hello@check-position.com\">skontaktuj się z nami</a> podając swój kraj i numer VAT Intracom.",
		"create-order": "Tworzenie Twojego zamówienia ...",
		"downgrade-at-renew": "Obniżenie przy odnowieniu",
		"edit-estimation": "Szacowanie modyfikacji ...",
		"edit-offer": "Chciałbym natychmiastowej modyfikacji mojej oferty.",
		"immediate-downgrade": "Obniż natychmiast",
		"offer": {
			"downgrade": "Obniż ofertę",
			"downgrade-renew": "Twoja oferta zostanie obniżona przy następnym odnowieniu dnia",
			"upgrade": "Podwyższ ofertę",
			"upgrade-renew": "Podwyższenie Twojej oferty jest natychmiastowe i generuje fakturę proporcjonalną do czasu pozostałego do następnego odnowienia dnia"
		},
		"payment-loading": "Oczekiwanie na płatność (prosimy o cierpliwość, może to potrwać kilka sekund) ...",
		"payment-loading-refresh": "Płatność jest nadal oczekująca, poczekaj kilka minut, a następnie odśwież stronę.",
		"subscription-update": "Aktualizacja Twojej subskrypcji ...",
		"success-payment": "Płatność pomyślnie zatwierdzona!",
		"warning-edit": "Uwaga, natychmiastowa modyfikacja Twojej oferty jest bezpłatna i nieodwołalna, ale nie wiąże się z żadnym zwrotem za pozostały okres.",
		"warning-error": "Wystąpił nieznany błąd. Spróbuj ponownie za chwilę."
	}
}
</i18n>
